import React from 'react';
import { isMobile } from 'react-device-detect';
import { BulkSelectWrapper, Indicator, Background } from './style';
import useTranslateWindowPointToCanvas from 'components/canvas/hooks/use-translate-window-point-to-canvas';
import useFetchSurfaceNodesFromCache from 'components/canvas/hooks/use-fetch-surface-nodes-from-cache';
import useNodeDimensionsOperations from 'components/canvas/hooks/use-node-dimensions-operations';
import useCurrentSurfacePermissions from 'components/canvas/hooks/use-current-surface-permissions';
import useSelectionRectangleOperations from 'components/canvas/components/bulk-select-provider/components/selection-rectangle/hooks';
import useIsCanvasDraggable from 'components/canvas/hooks/use-is-canvas-draggable';
import { useParams } from 'react-router';
import { useCanvasStageOperations } from 'state/canvas/stage';
import { useSelectedArea } from 'state/canvas/selected-area';
import { useSideDrawerState } from 'state/side-drawer';
import { notEqual } from 'utils/js';
import { isEmpty } from 'lodash';
import { Dimension } from 'types';
import { useIsLinkArrowShown } from 'state/canvas/node-linking';
import { useIsNodeDragged } from 'components/canvas/hooks/use-dragging-node-id';
import { Position } from 'graphql/types';
import { ToolContext } from '../paint-menu';

export interface CanvasSelectionBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface DOMSelectionBox {
  top: string;
  left: string;
  width: string;
  height: string;
}

interface Point {
  x: number;
  y: number;
}

interface BulkSelectProps {
  children?: any;
  onSelect?: Function;
}

export function translateDimensionsToCanvas(
  dimensions: Dimension,
  stage,
): Dimension {
  const scale = stage.scale;

  return {
    width: dimensions.width / scale,
    height: dimensions.height / scale,
  };
}

function getPointerPosition(event) {
  const x = event.targetTouches
    ? event.targetTouches[0].clientX
    : event.clientX;

  const y = event.targetTouches
    ? event.targetTouches[0].clientY
    : event.clientY;

  return {
    x,
    y,
  };
}

function calculateDimensions(origin: Point, current: Point) {
  return {
    width: current.x > origin.x ? current.x - origin.x : origin.x - current.x,
    height: current.y > origin.y ? current.y - origin.y : origin.y - current.y,
  };
}

function generateBox(origin: Point, current: Point) {
  const ORIGIN_PADDING = 1; // This is so that bulk select doesn't break onClick on node elements
  const { width, height } = calculateDimensions(origin, current);
  const { x: oX, y: oY } = origin;
  const { x: cX, y: cY } = current;
  const x = oX < cX ? oX : cX;
  const y = oY < cY ? oY : cY;

  return {
    top: `${y + ORIGIN_PADDING}px`,
    left: `${x + ORIGIN_PADDING}px`,
    width: `${width}px`,
    height: `${height}px`,
  };
}

function castDimensionsToNumber(
  dimensions: DOMSelectionBox,
): CanvasSelectionBox {
  return {
    x: parseInt(dimensions.left, 10),
    y: parseInt(dimensions.top, 10),
    width: parseInt(dimensions.width, 10),
    height: parseInt(dimensions.height, 10),
  };
}

function translateToCanvas(numeric, stage) {
  return {
    x: (numeric.x - stage.x) / stage.scale,
    y: (numeric.y - stage.y) / stage.scale,
    width: numeric?.width / stage.scale,
    height: numeric?.height / stage.scale,
  };
}

const defaultPosition = {
  x: 0,
  y: 0,
};

function BulkSelectProvider({ children, onSelect }: BulkSelectProps) {
  const { id } = useParams() as { id: string };

  const [origin, setStart] = React.useState<Point>(defaultPosition);

  const [current, setCurrent] = React.useState<Point>(defaultPosition);

  const { getCanvasStage } = useCanvasStageOperations();

  const translatePointToCanvas = useTranslateWindowPointToCanvas(id as string);

  const fetchSurfaceNodesFromCache = useFetchSurfaceNodesFromCache(id);

  const { selectionRectangleInstance } = useSelectionRectangleOperations(id);

  const { getNodeDimensions } = useNodeDimensionsOperations();

  const [
    selectedArea,
    { setSelectedArea, clearSelectedArea },
  ] = useSelectedArea();

  const {
    canCopySurfaceNode,
    canCutSurfaceNode,
    canRepositionSurfaceNode,
    canDeleteAny,
  } = useCurrentSurfacePermissions();

  const [node] = useSideDrawerState();

  const [isDraggable] = useIsCanvasDraggable();

  const isLinkArrowShown = useIsLinkArrowShown();

  const isNodeDragged = useIsNodeDragged();

  const { selectedTool } = React.useContext(ToolContext);

  const bulkSelectIsActive =
    isDraggable === false &&
    isLinkArrowShown === false &&
    isNodeDragged === false &&
    selectedTool == null;

  const isActive =
    bulkSelectIsActive &&
    (canDeleteAny ||
      canCopySurfaceNode() ||
      canCutSurfaceNode() ||
      canRepositionSurfaceNode());

  const dimensions: DOMSelectionBox = generateBox(origin, current);

  const selectionInProgress = notEqual(origin, defaultPosition);

  const showSelectionProgress =
    isActive && selectionInProgress && notEqual(current, defaultPosition);

  const isSideDrawerClosed = !node;

  React.useEffect(() => {
    if (isActive) {
      setStart(defaultPosition);
      setCurrent(defaultPosition);
      clearSelectedArea();
    }
  }, [isActive]);

  function onStart(event) {
    if (isActive === false) {
      return;
    }

    const position = { x: event.pageX, y: event.pageY };

    if (selectedArea) {
      if (clickedOnSelectionRectangle(position)) {
        return;
      }

      return clearSelectedArea();
    }

    // Most efficient hack I could think of
    const modalsAreClosed = isEmpty(
      document.getElementsByClassName('ReactModal__Body--open'),
    );

    if (modalsAreClosed && isSideDrawerClosed && notClickedOverNode(position)) {
      setStart(getPointerPosition(event));
    }
  }

  function onMouseMove(event) {
    if (isActive === false) {
      return;
    }

    if (selectionInProgress) {
      setCurrent(getPointerPosition(event));
    }
  }

  function onEnd() {
    if (isActive === false) {
      return;
    }

    if (selectionInProgress) {
      if (notEqual(current, defaultPosition)) {
        const stage = getCanvasStage();

        setSelectedArea(
          translateToCanvas(castDimensionsToNumber(dimensions), stage),
        );
        onSelect && onSelect();
      }

      setStart(defaultPosition);
      setCurrent(defaultPosition);
    }
  }

  function clickedOnSelectionRectangle(position: Position) {
    if (selectionRectangleInstance) {
      const stage = getCanvasStage();
      // @ts-ignore
      const selectionRectangleAbsolutePosition = selectionRectangleInstance?.absolutePosition();

      const xStart = selectionRectangleAbsolutePosition?.x;

      const width = selectionRectangleInstance?.width() * stage.scale;
      const height = selectionRectangleInstance?.height() * stage.scale;

      const xEnd = selectionRectangleAbsolutePosition?.x + width;

      const yStart = selectionRectangleAbsolutePosition?.y;

      const yEnd = selectionRectangleAbsolutePosition?.y + height;

      const isOnX = position.x >= xStart && position.x <= xEnd;
      const isOnY = position.y >= yStart && position.y <= yEnd;

      return isOnX && isOnY;
    }

    return false;
  }

  function notClickedOverNode(windowPosition: Position) {
    const canvasPosition = translatePointToCanvas(windowPosition);

    const clickedOverNode = fetchSurfaceNodesFromCache().some(
      ({ position, type }) => {
        const dimensions = getNodeDimensions(type);

        const isOnX =
          canvasPosition.x >= position.x &&
          canvasPosition.x <= position.x + dimensions.width;

        const offsetY = 50;

        const isOnY =
          canvasPosition.y >= position.y - offsetY &&
          canvasPosition.y <= position.y + dimensions.height;

        return isOnX && isOnY;
      },
    );

    return clickedOverNode === false;
  }

  const listeners = {
    ...(isMobile
      ? { onTouchStart: onStart, onTouchEnd: onEnd, onTouchMove: onMouseMove }
      : { onMouseDown: onStart, onMouseUp: onEnd, onMouseMove }),
  };

  return (
    <BulkSelectWrapper {...(isActive ? listeners : {})}>
      {children}
      {showSelectionProgress && (
        <Indicator style={dimensions}>
          <Background />
        </Indicator>
      )}
    </BulkSelectWrapper>
  );
}

export default BulkSelectProvider;
